import { createBrowserRouter, createMemoryRouter, type RouteObject } from "react-router";

import { RequireAuth } from "@/auth/guards";
import { AcceptInvitePage } from "@/features/auth/AcceptInvitePage";
import { ForgotPage } from "@/features/auth/ForgotPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { ResetPage } from "@/features/auth/ResetPage";
import { LoadingState } from "@/components/EmptyState";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { NotFoundPage } from "@/pages/not-found";
import { COMING_SOON_SPOTS, type FeatureFlag } from "@/lib/flags/flags";
import { SectionPage } from "@/pages/section";

import { RouteErrorBoundary } from "./ErrorBoundary";
import { AppLayout, type RouteHandle } from "./layouts/AppLayout";
import { EditorLayout } from "./layouts/EditorLayout";
import { allNavItems } from "./nav";
import { contentPaths, contentShellRoutes, editorFullScreenRoutes } from "./routes/content";
import { settingsRoutes, settingsPaths } from "./routes/settings";

/** Screens implemented in this app (replace the placeholder of their menu entry). */
const OWN_ROUTES: RouteObject[] = [
  // T11-10: to-do inbox + 4 big buttons + KPI
  { index: true, element: <DashboardPage />, handle: { titleKey: "nav.items.home" } },
  {
    // T11-11
    path: "products",
    handle: {
      access: { anyOf: ["products.write", "products.publish"] },
      titleKey: "nav.items.products_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/products/ProductsPage")).ProductsPage,
    }),
  },
  {
    // T11-12 "add a card in 2 minutes"
    path: "products/new",
    handle: {
      access: { permission: "products.write" },
      titleKey: "home.add_card",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/products/quick-create/QuickCreateWizard"))
        .QuickCreateWizard,
    }),
  },
  {
    // T11-13 trash (30 days)
    path: "products/trash",
    handle: {
      access: { permission: "products.write" },
      titleKey: "nav.items.products_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/products/edit/TrashPage")).TrashPage,
    }),
  },
  {
    // T11-13 the product form (read-only overview for staff without products.write)
    path: "products/:productId",
    handle: {
      access: { permission: "products.read" },
      titleKey: "nav.items.products_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/products/edit/ProductEditPage")).ProductEditPage,
    }),
  },
  // T11-14 card catalog (games → sets → cards)
  {
    path: "products/catalog",
    handle: {
      access: { anyOf: ["catalog.write", "products.write"] },
      titleKey: "nav.items.products_catalog",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/catalog/GamesPage")).GamesPage }),
  },
  {
    path: "products/catalog/games/:gameId",
    handle: {
      access: { anyOf: ["catalog.write", "products.write"] },
      titleKey: "nav.items.products_catalog",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/catalog/SetsPage")).SetsPage }),
  },
  {
    path: "products/catalog/sets/:setId",
    handle: {
      access: { anyOf: ["catalog.write", "products.write"] },
      titleKey: "nav.items.products_catalog",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/catalog/CardsPage")).CardsPage }),
  },
  // T11-15 collections (manual order / smart rules)
  {
    path: "products/collections",
    handle: {
      access: { anyOf: ["collections.write", "products.write"] },
      titleKey: "nav.items.products_collections",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/collections/CollectionsPage")).CollectionsPage,
    }),
  },
  {
    path: "products/collections/:collectionId",
    handle: {
      access: { anyOf: ["collections.write", "products.write"] },
      titleKey: "nav.items.products_collections",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/collections/CollectionEditPage")).CollectionEditPage,
    }),
  },
  // T11-16 extra product details, categories, brands (list + side panel form)
  {
    path: "products/attributes",
    handle: {
      access: { permission: "catalog.write" },
      titleKey: "nav.items.products_attributes",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/catalog/AttributeDefinitionsPage"))
        .AttributeDefinitionsPage,
    }),
  },
  {
    path: "products/categories",
    handle: {
      access: { permission: "catalog.write" },
      titleKey: "nav.items.products_categories",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/catalog/CategoriesPage")).CategoriesPage,
    }),
  },
  {
    path: "products/brands",
    handle: {
      access: { permission: "catalog.write" },
      titleKey: "nav.items.products_brands",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/catalog/BrandsPage")).BrandsPage }),
  },
  // T11-17 CSV import wizard (job id in the URL) + export
  {
    path: "products/import-export",
    handle: {
      access: { permission: "import.run" },
      titleKey: "nav.items.products_import_export",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/import-export/ImportExportPage")).ImportExportPage,
    }),
  },
  // T11-18 … T11-20 inventory
  {
    path: "inventory",
    handle: {
      access: { anyOf: ["inventory.receive", "inventory.adjust", "inventory.holds.manage"] },
      titleKey: "nav.items.inventory_stock",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/InventoryPage")).InventoryPage,
    }),
  },
  {
    path: "inventory/locations",
    handle: {
      access: { anyOf: ["inventory.receive", "inventory.adjust"] },
      titleKey: "nav.items.inventory_locations",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/LocationsPage")).LocationsPage,
    }),
  },
  {
    path: "inventory/movements",
    handle: {
      access: { anyOf: ["inventory.receive", "inventory.adjust", "inventory.holds.manage"] },
      titleKey: "nav.items.inventory_movements",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/MovementsPage")).MovementsPage,
    }),
  },
  {
    path: "inventory/receipts",
    handle: {
      access: { permission: "inventory.receive" },
      titleKey: "nav.items.inventory_receipts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/ReceivePage")).ReceivePage,
    }),
  },
  {
    path: "inventory/transfers",
    handle: {
      access: { permission: "inventory.transfer" },
      titleKey: "nav.items.inventory_transfers",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/TransfersPage")).TransfersPage,
    }),
  },
  {
    path: "inventory/transfers/new",
    handle: {
      access: { permission: "inventory.transfer" },
      titleKey: "nav.items.inventory_transfers",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/TransferEditPage")).TransferEditPage,
    }),
  },
  {
    path: "inventory/transfers/:transferId",
    handle: {
      access: { permission: "inventory.transfer" },
      titleKey: "nav.items.inventory_transfers",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/TransferEditPage")).TransferEditPage,
    }),
  },
  {
    path: "inventory/transfers/:transferId/receive",
    handle: {
      access: { permission: "inventory.transfer" },
      titleKey: "nav.items.inventory_transfers",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/TransferReceivePage")).TransferReceivePage,
    }),
  },
  {
    path: "inventory/counts",
    handle: {
      access: { permission: "inventory.count" },
      titleKey: "nav.items.inventory_counts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/CountsPage")).CountsPage,
    }),
  },
  {
    path: "inventory/counts/:countId",
    handle: {
      access: { permission: "inventory.count" },
      titleKey: "nav.items.inventory_counts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/CountSummary")).CountSummaryPage,
    }),
  },
  {
    path: "inventory/counts/:countId/scan",
    handle: {
      access: { permission: "inventory.count" },
      titleKey: "nav.items.inventory_counts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/inventory/CountScanMode")).CountScanMode,
    }),
  },
  // T11-21 holds + sold-twice cards
  {
    path: "inventory/holds",
    handle: {
      access: { anyOf: ["inventory.holds.release", "inventory.holds.manage"] },
      titleKey: "nav.items.inventory_holds",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/inventory/HoldsPage")).HoldsPage }),
  },
  {
    path: "inventory/conflicts",
    handle: {
      access: { permission: "inventory.conflicts.resolve" },
      titleKey: "nav.items.inventory_conflicts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/ConflictsPage")).ConflictsPage,
    }),
  },
  {
    path: "inventory/conflicts/:conflictId",
    handle: {
      access: { permission: "inventory.conflicts.resolve" },
      titleKey: "nav.items.inventory_conflicts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/ConflictDetail")).ConflictDetail,
    }),
  },
  // T11-22 … T11-25 orders
  {
    path: "orders",
    handle: {
      access: { permission: "orders.read" },
      titleKey: "nav.items.orders_all",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/orders/OrdersPage")).OrdersPage }),
  },
  {
    path: "orders/new",
    handle: {
      access: { permission: "orders.update" },
      titleKey: "nav.items.orders_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/DraftOrderPage")).DraftOrderPage,
    }),
  },
  {
    path: "orders/ship",
    handle: {
      access: { permission: "orders.fulfill" },
      titleKey: "nav.items.orders_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/fulfillment/BulkShipPage")).BulkShipPage,
    }),
  },
  {
    path: "orders/bank-transfers",
    handle: {
      access: { permission: "payments.confirm" },
      titleKey: "nav.items.orders_bank_transfers",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/payments/PendingTransfersPage")).PendingTransfersPage,
    }),
  },
  {
    path: "orders/pickups",
    handle: {
      access: { permission: "orders.fulfill" },
      titleKey: "nav.items.orders_pickups",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/fulfillment/PickupsPage")).PickupsPage,
    }),
  },
  {
    // T11-26 returns of every order
    path: "orders/returns",
    handle: {
      access: { permission: "orders.update" },
      titleKey: "nav.items.orders_returns",
    } satisfies RouteHandle,
    lazy: async () => ({ Component: (await import("@/features/orders/ReturnsPage")).ReturnsPage }),
  },
  {
    // T11-27 documents of one order
    path: "orders/:orderId/documents",
    handle: {
      access: { permission: "orders.read" },
      titleKey: "nav.items.orders_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/documents/DocumentsPage")).DocumentsPage,
    }),
  },
  {
    path: "orders/:orderId",
    handle: {
      access: { anyOf: ["orders.read", "consignment.read"] },
      titleKey: "nav.items.orders_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/orders/detail/OrderDetailPage")).OrderDetailPage,
    }),
  },
  // T11-28 customers (the register role sees names only)
  {
    path: "customers",
    handle: {
      access: { anyOf: ["customers.write", "consignment.read"] },
      titleKey: "nav.items.customers_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/customers/CustomersPage")).CustomersPage,
    }),
  },
  {
    path: "customers/:customerId",
    handle: {
      access: { anyOf: ["customers.read", "consignment.read"] },
      titleKey: "nav.items.customers_all",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/customers/CustomerDetailPage")).CustomerDetailPage,
    }),
  },
  // T11-29 discount codes (everyone may look — design/04 §9; changes need discounts.write)
  {
    path: "discounts",
    handle: {
      access: { anyOf: ["discounts.write", "orders.read"] },
      titleKey: "nav.items.discounts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/discounts/DiscountsPage")).DiscountsPage,
    }),
  },
  {
    path: "discounts/new",
    handle: {
      access: { permission: "discounts.write" },
      titleKey: "nav.items.discounts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/discounts/DiscountEditPage")).DiscountEditPage,
    }),
  },
  {
    path: "discounts/:discountId",
    handle: {
      access: { anyOf: ["discounts.write", "orders.read"] },
      titleKey: "nav.items.discounts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/discounts/DiscountEditPage")).DiscountEditPage,
    }),
  },
  // T11-30 consignors + 本人確認
  {
    path: "consignment/consignors",
    handle: {
      access: { permission: "consignment.read" },
      titleKey: "nav.items.consignment_consignors",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/ConsignorsPage")).ConsignorsPage,
    }),
  },
  {
    path: "consignment/consignors/new",
    handle: {
      access: { permission: "consignment.manage" },
      titleKey: "nav.items.consignment_consignors",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/ConsignorNewPage")).ConsignorNewPage,
    }),
  },
  {
    path: "consignment/consignors/:consignorId",
    handle: {
      access: { permission: "consignment.read" },
      titleKey: "nav.items.consignment_consignors",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/ConsignorDetailPage")).ConsignorDetailPage,
    }),
  },
  // T11-31 consignment intake, consigned cards (board + price approvals), requests
  {
    path: "consignment/intake",
    handle: {
      access: { permission: "consignment.intake" },
      titleKey: "nav.items.consignment_items",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/IntakeSession")).IntakeSession,
    }),
  },
  {
    path: "consignment/items",
    handle: {
      access: { anyOf: ["consignment.read", "consignment.intake"] },
      titleKey: "nav.items.consignment_items",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/ItemsBoard")).ItemsBoard,
    }),
  },
  {
    path: "consignment/requests",
    handle: {
      access: { permission: "consignment.manage" },
      titleKey: "nav.items.consignment_requests",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/RequestsPage")).RequestsPage,
    }),
  },
  // T11-32 settlements + payouts (wizard, detail with mark paid / 精算書)
  {
    path: "consignment/settlements",
    handle: {
      access: { permission: "consignment.manage" },
      titleKey: "nav.items.consignment_settlements",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/SettlementsPage")).SettlementsPage,
    }),
  },
  {
    path: "consignment/payouts",
    handle: {
      access: { anyOf: ["payouts.create", "consignment.read"] },
      titleKey: "nav.items.consignment_payouts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/PayoutsPage")).PayoutsPage,
    }),
  },
  {
    path: "consignment/payouts/new",
    handle: {
      access: { permission: "payouts.create" },
      titleKey: "nav.items.consignment_payouts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/PayoutWizard")).PayoutWizard,
    }),
  },
  {
    path: "consignment/payouts/:payoutId",
    handle: {
      access: { permission: "consignment.read" },
      titleKey: "nav.items.consignment_payouts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/PayoutDetail")).PayoutDetail,
    }),
  },
  // T11-33 古物台帳 (read-only; export 様式15/16/CSV)
  {
    path: "consignment/kobutsu-ledger",
    handle: {
      access: { anyOf: ["kobutsu.read", "consignment.read"] },
      titleKey: "nav.items.consignment_kobutsu",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/consignment/KobutsuLedgerPage")).KobutsuLedgerPage,
    }),
  },
  // T11-34 / T11-35 POS admin (registers + pairing + PINs, shifts + Z-report, cash, buy-ins, rules)
  {
    path: "pos/registers",
    handle: {
      access: { anyOf: ["pos.devices.pair", "settings.read"] },
      titleKey: "nav.items.pos_registers",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/pos-admin/RegistersPage")).RegistersPage,
    }),
  },
  {
    path: "pos/shifts",
    handle: {
      access: { anyOf: ["pos.shift.manage", "reports.view"] },
      titleKey: "nav.items.pos_shifts",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/pos-admin/ShiftsPage")).ShiftsPage,
    }),
  },
  {
    path: "pos/cash-movements",
    handle: {
      access: { anyOf: ["pos.shift.manage", "reports.view"] },
      titleKey: "nav.items.pos_cash",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/pos-admin/ShiftsPage")).CashMovementsPage,
    }),
  },
  {
    path: "pos/buylist",
    handle: {
      access: { permission: "buylist.accept" },
      titleKey: "nav.items.pos_buylist",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/pos-admin/BuylistIntakesPage")).BuylistIntakesPage,
    }),
  },
  {
    path: "settings/pos",
    handle: {
      access: { permission: "settings.read" },
      titleKey: "nav.items.settings_pos",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/pos-admin/PosRulesPage")).PosRulesPage,
    }),
  },
  {
    // T11-36 tax settings + legal calendar (?tab=calendar)
    path: "settings/tax",
    handle: {
      access: { anyOf: ["tax.settings", "tax.read"] },
      titleKey: "nav.items.settings_tax",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/tax/TaxSettingsPage")).TaxSettingsPage,
    }),
  },
  {
    // T11-37 免税 records (?status= tab)
    path: "settings/tax/tax-free",
    handle: {
      access: { anyOf: ["tax.read", "tax.taxfree"] },
      titleKey: "nav.items.settings_tax",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/tax/TaxFreeRecordsPage")).TaxFreeRecordsPage,
    }),
  },
  {
    // T11-37 免税販売 monthly report (?month=YYYY-MM)
    path: "settings/tax/tax-free/report",
    handle: {
      access: { permission: "tax.read" },
      titleKey: "nav.items.settings_tax",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/tax/TaxFreeMonthlyReportPage")).TaxFreeMonthlyReportPage,
    }),
  },
  {
    // T13-16 電帳法 document search (?from=&to=&amountMin=&amountMax=&counterparty=&q=)
    path: "settings/tax/documents",
    handle: {
      // = DOCUMENT_READ_PERMISSIONS (features/documents/api.ts; media app/documents/access.py)
      access: {
        anyOf: ["orders.read", "consignment.read", "tax.read", "reports.view", "kobutsu.export"],
      },
      titleKey: "nav.items.settings_tax",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/documents/DocumentSearchPage")).DocumentSearchPage,
    }),
  },
  {
    // T11-38 輸出免税・証憑待ち
    path: "orders/export-evidence",
    handle: {
      access: { permission: "tax.read" },
      titleKey: "nav.items.orders_export_evidence",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/tax/ExportEvidenceQueuePage")).ExportEvidenceQueuePage,
    }),
  },
  {
    // T11-38 the proof of one export order (the order page links here)
    path: "orders/:orderId/export-evidence",
    handle: {
      access: { permission: "tax.read" },
      titleKey: "nav.items.orders_export_evidence",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/tax/ExportEvidencePage")).ExportEvidencePage,
    }),
  },
  {
    // T11-41 zones + prices, carriers, import duties, packing, "what would a customer see" (?tab=)
    path: "settings/shipping",
    handle: {
      access: { permission: "settings.read" },
      titleKey: "nav.items.settings_shipping",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/shipping/ShippingSettingsPage")).ShippingSettingsPage,
    }),
  },
  {
    // T11-39 ways to take money + card terminal fees
    path: "settings/payments",
    handle: {
      access: { permission: "payments.read" },
      titleKey: "nav.items.settings_payments",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/payments/PaymentSettingsPage")).PaymentSettingsPage,
    }),
  },
  {
    // T11-40 card terminal statement check (?batch=<id>)
    path: "settings/payments/reconciliation",
    handle: {
      access: { permission: "payments.reconcile" },
      titleKey: "nav.items.settings_payments",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/payments/ReconciliationWizard")).ReconciliationWizard,
    }),
  },
  {
    // T11-40 every refund
    path: "settings/payments/refunds",
    handle: {
      access: { permission: "payments.read" },
      titleKey: "nav.items.settings_payments",
    } satisfies RouteHandle,
    lazy: async () => ({
      Component: (await import("@/features/payments/RefundsPage")).RefundsPage,
    }),
  },
  {
    // T11-47 help: guides + words of tax/law ("Read more" of every "?")
    path: "help",
    handle: { titleKey: "nav.items.help" },
    lazy: async () => ({ Component: (await import("@/features/help/HelpPage")).HelpPage }),
  },
  {
    path: "help/:topic",
    handle: { titleKey: "nav.items.help" },
    lazy: async () => ({ Component: (await import("@/features/help/HelpPage")).HelpPage }),
  },
  {
    path: "profile",
    handle: { titleKey: "nav.items.profile" },
    lazy: async () => ({ Component: (await import("@/features/auth/ProfilePage")).ProfilePage }),
  },
  {
    path: "profile/two-step",
    handle: { titleKey: "nav.items.profile" },
    lazy: async () => ({
      Component: (await import("@/features/auth/TwoStepSetupPage")).TwoStepSetupPage,
    }),
  },
];
const ownPaths = new Set([
  "/",
  "/help",
  "/profile",
  "/products",
  "/products/catalog",
  "/products/collections",
  "/products/attributes",
  "/products/categories",
  "/products/brands",
  "/products/import-export",
  "/inventory",
  "/inventory/locations",
  "/inventory/movements",
  "/inventory/receipts",
  "/inventory/transfers",
  "/inventory/counts",
  "/inventory/holds",
  "/inventory/conflicts",
  "/orders",
  "/orders/bank-transfers",
  "/orders/pickups",
  "/orders/returns",
  "/customers",
  "/discounts",
  "/consignment/consignors",
  "/consignment/items",
  "/consignment/requests",
  "/consignment/settlements",
  "/consignment/payouts",
  "/consignment/kobutsu-ledger",
  "/pos/registers",
  "/pos/shifts",
  "/pos/cash-movements",
  "/pos/buylist",
  "/settings/pos",
  "/settings/tax",
  "/settings/payments",
  "/settings/shipping",
  "/orders/export-evidence",
]);

/** The "coming soon" integrations design/04 puts on a screen (T11-09 checklist). */
function featuresAt(path: string): FeatureFlag[] {
  return [
    ...new Set(COMING_SOON_SPOTS.filter((spot) => spot.where === path).map((s) => s.feature)),
  ];
}

/** Every other menu entry opens its page frame now; the screens arrive task by task (T11-10…). */
function placeholderRoutes(): RouteObject[] {
  return allNavItems()
    .filter(
      (item) => !ownPaths.has(item.to) && !contentPaths.has(item.to) && !settingsPaths.has(item.to),
    )
    .map((item) => ({
      path: item.to.slice(1),
      handle: { access: item.access, titleKey: `nav.items.${item.key}` } satisfies RouteHandle,
      element: (
        <SectionPage
          titleKey={`nav.items.${item.key}`}
          comingSoon={item.comingSoon}
          features={item.to === "/settings/channels" ? CHANNEL_FEATURES : featuresAt(item.to)}
        />
      ),
    }));
}

const CHANNEL_FEATURES: FeatureFlag[] = [
  "channels.ebay",
  "channels.whatnot",
  "channels.mercari",
  "channels.tiktok_shop",
];

/**
 * Addresses of phase-gated screens (R18, T11-09): while the flag is off they open the "coming
 * soon" page (route guard in AccessOutlet) instead of a 404; T20–T22 replace the element.
 */
const FEATURE_ROUTES: RouteObject[] = [
  ...(["ebay", "whatnot", "mercari", "tiktok-shop"] as const).map((channel) => ({
    path: `settings/channels/${channel}/*`,
    handle: {
      access: { permission: "channels.manage" },
      titleKey: "nav.items.settings_channels",
      feature: `channels.${channel.replace("-", "_")}` as FeatureFlag,
    } satisfies RouteHandle,
    element: <SectionPage titleKey="nav.items.settings_channels" />,
  })),
  {
    // design/04 §1 "Channels" group (listings, channel orders, price rules — T21/T22)
    path: "channels/*",
    handle: {
      access: { permission: "channels.manage" },
      titleKey: "nav.items.settings_channels",
      feature: CHANNEL_FEATURES,
    } satisfies RouteHandle,
    element: <SectionPage titleKey="nav.items.settings_channels" />,
  },
  {
    path: "settings/payments/stripe",
    handle: {
      access: { permission: "payments.settings" },
      titleKey: "nav.items.settings_payments",
      feature: "payments.stripe",
    } satisfies RouteHandle,
    element: <SectionPage titleKey="nav.items.settings_payments" />,
  },
  {
    // "Send payment link" of a draft order (design/04 §6, T20-08)
    path: "orders/payment-links",
    handle: {
      access: { permission: "orders.update" },
      titleKey: "nav.items.orders_drafts",
      feature: "payments.stripe",
    } satisfies RouteHandle,
    element: <SectionPage titleKey="nav.items.orders_drafts" />,
  },
];

/** Dev builds (and `VITE_COMPONENT_GALLERY=true`): the component gallery used by the e2e suite. */
const DEV_ROUTES: RouteObject[] =
  import.meta.env.DEV || import.meta.env.VITE_COMPONENT_GALLERY === "true"
    ? [
        {
          path: "_dev/components",
          lazy: async () => ({
            Component: (await import("@/dev/ComponentGallery")).ComponentGallery,
          }),
        },
      ]
    : [];

/**
 * Screens reached from buttons (not the menu) that are built in later tasks — each opens its
 * frame (`SectionPage`) until the task arrives. Empty since T11-37/38 built the last two.
 */
const LATER_ROUTES: RouteObject[] = [];

export function buildRoutes(): RouteObject[] {
  return [
    { path: "/login", element: <LoginPage /> },
    { path: "/forgot", element: <ForgotPage /> },
    { path: "/reset", element: <ResetPage /> },
    // the link notification-service puts in the staff reset e-mail (T01-03) — same page
    { path: "/reset-password", element: <ResetPage /> },
    // T01-18: the invitation e-mail's "choose your password" link
    { path: "/invite", element: <AcceptInvitePage /> },
    {
      element: <RequireAuth />,
      // first paint of a lazy screen (e.g. opening /profile directly)
      hydrateFallbackElement: <LoadingState />,
      children: [
        // full-screen screens without the admin menu (theme editor — T09-20, T11-42)
        {
          element: <EditorLayout />,
          errorElement: <RouteErrorBoundary />,
          children: [...editorFullScreenRoutes],
        },
        {
          element: <AppLayout />,
          children: [
            {
              errorElement: <RouteErrorBoundary />,
              children: [
                ...OWN_ROUTES,
                ...contentShellRoutes,
                ...settingsRoutes,
                ...placeholderRoutes(),
                ...LATER_ROUTES,
                ...FEATURE_ROUTES,
                ...DEV_ROUTES,
                { path: "*", element: <NotFoundPage /> },
              ],
            },
          ],
        },
      ],
    },
  ];
}

export const routes: RouteObject[] = buildRoutes();

export function createAppRouter() {
  return createBrowserRouter(routes);
}

export function createTestRouter(initialEntries: string[]) {
  return createMemoryRouter(routes, { initialEntries });
}
